// web/src/shared/keynav/KeyNavRow.tsx
import { useEffect, useRef, ReactNode, KeyboardEvent } from 'react';

type Props = {
  children: ReactNode;
  // Default 'horizontal'. Vertical for dropdown menu items.
  layout?: 'horizontal' | 'vertical';
  // Tailwind classes for the wrapper div. Defaults to passthrough.
  className?: string;
  // ARIA role. Defaults to 'toolbar' for horizontal, 'menu' for vertical.
  role?: string;
  ariaLabel?: string;
  // Where ArrowUp exits to. 'subnav' (used by the page toolbars) jumps to the
  // ACTIVE second-nav tab; default walks to the nearest preceding focusable.
  stepUpTo?: 'subnav' | 'auto';
};

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"]):not([tabindex="-2"])',
  '[role="button"]:not([aria-disabled="true"])',
].join(',');

// True for elements whose own arrow / Space behavior should win.
function isInteractiveTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  // Opt-in: a control marked data-keynav-travel wants the row's arrow keys to
  // move focus between toolbar items rather than change its own value (e.g. the
  // filter-chip <select>s — you open them with Enter / Backspace / click, and
  // travel past them with the arrows).
  if (el.hasAttribute('data-keynav-travel')) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  if (el.isContentEditable) return true;
  return false;
}

// Wraps a region whose interactive descendants should be reachable
// with arrow keys via the roving-tabindex pattern. Discovers the
// focusable set at mount and on every DOM mutation, so conditional
// children (e.g. an Export button that only renders when a callback is
// provided) participate without manual wiring.
//
// Tab/Shift-Tab leaves the row in DOM order (only the focused element
// has tabindex=0; the rest are -1).
export function KeyNavRow({ children, layout = 'horizontal', className, role, ariaLabel, stepUpTo = 'auto' }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Index we're "logically" focused at. The DOM is the source of truth
  // for which element actually has focus; we track an index so re-mounts
  // restore approximate position.
  const indexRef = useRef(0);

  function getItems(): HTMLElement[] {
    const root = ref.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter((el) => el.offsetParent !== null || el === document.activeElement)
      // Opt-out: controls marked data-keynav-skip (or nested under one) stay
      // click/Tab reachable but are excluded from arrow travel — e.g. the
      // right-hand Views / Export / Columns utilities.
      .filter((el) => !el.closest('[data-keynav-skip]'));
  }

  // Apply roving tabindex on every render and DOM mutation.
  // CRITICAL: do NOT include 'tabindex' in attributeFilter — applyRoving
  // writes tabIndex on items, which would re-trigger the observer and
  // produce an infinite loop, hanging the page. We are the only writer
  // of tabindex on these items, so we don't need to react to it.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    let scheduled = false;
    function applyRoving() {
      scheduled = false;
      const items = getItems();
      if (items.length === 0) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        const i = items.indexOf(active);
        if (i >= 0) indexRef.current = i;
      }
      const focused = Math.min(indexRef.current, items.length - 1);
      items.forEach((el, i) => {
        const want = i === focused ? 0 : -1;
        if (el.tabIndex !== want) el.tabIndex = want;
      });
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      // Defer so a burst of mutations collapses into one pass.
      Promise.resolve().then(applyRoving);
    }

    applyRoving();
    const mo = new MutationObserver(schedule);
    mo.observe(root, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'hidden'] });
    return () => mo.disconnect();
  }, []);

  function focusAt(i: number) {
    const items = getItems();
    if (items.length === 0) return;
    const next = Math.max(0, Math.min(i, items.length - 1));
    indexRef.current = next;
    items.forEach((el, j) => { el.tabIndex = j === next ? 0 : -1; });
    items[next].focus();
  }

  // Step focus to the next/prev focusable element OUTSIDE this row in
  // DOM order. Used for arrow-out: ArrowDown from a horizontal toolbar
  // should drop the user into the table below it, ArrowUp should pop
  // them back up into the SubNav above.
  function stepOutside(direction: 'next' | 'prev'): boolean {
    const root = ref.current;
    if (!root) return false;
    // Build the global focusable list across the whole document.
    const all = Array.from(document.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-2"]), [role="button"]:not([aria-disabled="true"]), [role="gridcell"], [role="columnheader"]'
    )).filter((el) => el.offsetParent !== null || el === document.activeElement);
    // Find candidates outside this row.
    if (direction === 'next') {
      // Everything after this row, in document order.
      const followers = all.filter((el) => !root.contains(el)
        && (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
      // Prefer stepping straight into a following data grid's BODY — its
      // remembered cell (tabindex=0) or first cell — so ArrowDown from the
      // toolbar lands on the table, not on a column-header sort/filter button.
      const cell = followers.find((el) => el.matches('[role="gridcell"][tabindex="0"]'))
        || followers.find((el) => el.matches('[role="gridcell"]'));
      if (cell) { cell.focus(); return true; }
      // Otherwise the first element that actually accepts focus (skip
      // non-focusable role-only nodes like bare column headers).
      for (const el of followers) {
        el.focus();
        if (document.activeElement === el) return true;
      }
      return false;
    } else {
      // Page toolbars pop straight back to the ACTIVE second-nav tab —
      // symmetric with SubNav's ArrowDown into the toolbar.
      if (stepUpTo === 'subnav') {
        const sub = document.querySelector<HTMLElement>('[data-subnav] a[aria-current="page"]')
          || document.querySelector<HTMLElement>('[data-subnav] a[href], [data-subnav] button');
        if (sub) { sub.focus(); if (document.activeElement === sub) return true; }
      }
      // Nearest focusable BEFORE the row — walking backwards and VERIFYING
      // the focus stuck (an element that silently refuses focus used to eat
      // the keystroke and the cursor vanished).
      const preceding = all.filter((el) => !root.contains(el)
        && (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING));
      for (let i = preceding.length - 1; i >= 0; i--) {
        preceding[i].focus();
        if (document.activeElement === preceding[i]) return true;
      }
      return false;
    }
  }

  function handleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (isInteractiveTarget(e.target)) return; // text input / textarea owns its own keys
    const items = getItems();
    if (items.length === 0) return;
    const active = document.activeElement;
    const cur = active instanceof HTMLElement ? items.indexOf(active) : -1;
    if (cur < 0) return;

    let next = cur;
    let handled = true;
    let stepped = false;
    switch (e.key) {
      case 'ArrowRight':
        if (layout === 'horizontal') next = Math.min(cur + 1, items.length - 1);
        else handled = false;
        break;
      case 'ArrowLeft':
        if (layout === 'horizontal') next = Math.max(cur - 1, 0);
        else handled = false;
        break;
      case 'ArrowDown':
        if (layout === 'vertical') next = Math.min(cur + 1, items.length - 1);
        else if (layout === 'horizontal') stepped = stepOutside('next');
        else handled = false;
        break;
      case 'ArrowUp':
        if (layout === 'vertical') next = Math.max(cur - 1, 0);
        else if (layout === 'horizontal') stepped = stepOutside('prev');
        else handled = false;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      if (!stepped) focusAt(next);
    }
  }

  return (
    <div
      ref={ref}
      role={role || (layout === 'horizontal' ? 'toolbar' : 'menu')}
      aria-label={ariaLabel}
      onKeyDown={handleKey}
      className={className}
    >
      {children}
    </div>
  );
}
